import { useMemo, useState, type ReactNode } from 'react'
import type { RosterFarmer } from '@/offline'
import type { DeliveryDraft } from '../../model/delivery'
import { farmerMatches } from '../../model/search'
import { Avatar, Btn, Footer, Icon, ItemRow, Tick, V1Header, V1Screen, WaitCta } from '../kit'
import { C } from '../style'
import { tr, rosterShort } from '../text'
import { V1Photo } from '../photo'

type Ident = DeliveryDraft['identification']

/** A farmer card in the results (green edge and मिला when it is the one). */
export function FarmerRow({
  f,
  village,
  found,
  note,
  onClick,
}: {
  f: RosterFarmer
  village: string
  found?: boolean
  /** A second line under his name (the bank page: his account's state). */
  note?: ReactNode
  onClick?: () => void
}) {
  return (
    <ItemRow
      onClick={onClick}
      border={found ? C.ok : C.rule}
      borderWidth={found ? 2 : 1.5}
      lead={<Avatar url={f.photo_url} size={44} radius={10} icon={37} />}
      titleSize={19}
      title={f.name}
      line={
        <span style={{ fontSize: 15, color: C.muted }}>
          {[
            f.father_name ? tr('gate.v1.father', { name: f.father_name }) : null,
            f.roster_no || village,
          ]
            .filter(Boolean)
            .join(' · ')}
          {note && <span style={{ display: 'block', marginTop: 2 }}>{note}</span>}
        </span>
      }
      right={
        found ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '3px 10px 3px 4px',
              borderRadius: 16,
              background: C.okWash,
              color: C.ok,
              fontWeight: 700,
              fontSize: 16,
              lineHeight: 1.25,
              alignSelf: 'flex-start',
              whiteSpace: 'nowrap',
            }}
          >
            <Tick />
            {tr('gate.v1.find.found')}
          </span>
        ) : undefined
      }
    />
  )
}

/** How many farmers show before she types or taps "show all" (as on the field POS). */
const QUICK = 30

/**
 * F4.2 — किसान खोजें: the village's farmers in a list, sorted by name; type his name (or father's
 * name, or farmer ID) to narrow it, tap him, then "क्या यही हैं?" with his photo. No draft exists
 * until she taps हाँ, यही हैं.
 */
export function FindFarmerV1({
  farmers,
  village,
  pending,
  onBack,
  onConfirm,
  busy,
}: {
  farmers: readonly RosterFarmer[]
  village: string
  pending: number
  onBack: () => void
  onConfirm: (f: RosterFarmer, identification: Ident) => void
  busy: boolean
}) {
  const [q, setQ] = useState('')
  const [all, setAll] = useState(false)
  const [picked, setPicked] = useState<{ f: RosterFarmer; shown: number } | null>(null)
  const matched = useMemo(
    () =>
      farmers.filter((f) => farmerMatches(f, q)).sort((a, b) => a.name.localeCompare(b.name, 'hi')),
    [farmers, q],
  )
  const shown = q.trim() || all ? matched : matched.slice(0, QUICK)
  const hidden = matched.length - shown.length

  if (picked) {
    return (
      <ConfirmFarmerV1
        f={picked.f}
        village={village}
        pending={pending}
        busy={busy}
        onNo={() => setPicked(null)}
        onYes={(newPhoto) =>
          onConfirm(picked.f, {
            method: 'SEARCH',
            phone_match: null,
            candidates_shown: picked.shown,
            new_photo_media_id: newPhoto,
          })
        }
      />
    )
  }

  return (
    <V1Screen
      main={{ gap: 8 }}
      header={
        <V1Header
          onBack={onBack}
          pending={pending}
          icon="user"
          title={tr('gate.v1.find.title')}
          step={1}
        />
      }
      footer={
        <Footer>
          <WaitCta>{tr('gate.v1.find.tapName')}</WaitCta>
        </Footer>
      }
    >
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 56,
          padding: '0 12px',
          borderRadius: 12,
          border: `2px solid ${C.acc}`,
          background: C.surface,
          flexShrink: 0,
        }}
      >
        <Icon name="search" size={28} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={tr('gate.v1.find.name')}
          aria-label={tr('gate.v1.find.name')}
          enterKeyHint="search"
          autoComplete="off"
          style={{
            flexGrow: 1,
            minWidth: 0,
            border: 0,
            outline: 'none',
            background: 'transparent',
            fontFamily: "'Mukta', sans-serif",
            fontSize: 22,
            fontWeight: 600,
            color: C.ink,
          }}
        />
        {q && (
          <button
            type="button"
            onClick={() => setQ('')}
            aria-label={tr('gate.v1.find.clear')}
            style={{
              border: 0,
              background: 'transparent',
              color: C.muted,
              padding: 6,
              cursor: 'pointer',
            }}
          >
            <Icon name="x" size={22} />
          </button>
        )}
      </label>
      <div style={{ fontSize: 15, color: C.muted, fontWeight: 600 }}>
        {q.trim()
          ? tr('gate.v1.find.matches', { n: matched.length })
          : tr('gate.v1.find.inVillage', { n: farmers.length, village })}
      </div>
      {matched.length === 0 ? (
        <div style={{ fontSize: 17, color: C.bad, fontWeight: 700 }}>{tr('gate.v1.find.none')}</div>
      ) : (
        shown.map((f) => (
          <FarmerRow
            key={f.user_id}
            f={f}
            village={village}
            found={matched.length === 1 && Boolean(q.trim())}
            onClick={() => setPicked({ f, shown: matched.length })}
          />
        ))
      )}
      {hidden > 0 && (
        <Btn icon="down" onClick={() => setAll(true)}>
          {tr('gate.v1.find.showAll', { n: hidden })}
        </Btn>
      )}
    </V1Screen>
  )
}

/** "क्या यही हैं?" — his registration photo, big; नहीं, ये नहीं / हाँ, यही हैं. */
function ConfirmFarmerV1({
  f,
  village,
  pending,
  onNo,
  onYes,
  busy,
}: {
  f: RosterFarmer
  village: string
  pending: number
  onNo: () => void
  onYes: (newPhotoMediaId: string | null) => void
  busy: boolean
}) {
  const [newPhoto, setNewPhoto] = useState<string | null>(null)
  const big = {
    minHeight: 88,
    borderRadius: 14,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    fontFamily: "'Mukta', sans-serif",
    cursor: 'pointer',
  }
  return (
    <V1Screen
      header={
        <V1Header
          onBack={onNo}
          pending={pending}
          icon="user"
          title={tr('gate.v1.confirm.title')}
          step={1}
        />
      }
      footer={
        <footer
          style={{
            padding: '8px 12px 12px',
            paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
            borderTop: `1px solid ${C.rule}`,
          }}
        >
          <div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}
          >
            <button
              type="button"
              onClick={onNo}
              style={{
                ...big,
                background: C.surface,
                border: `2.5px solid ${C.bad}`,
                color: C.bad,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 4,
                  background: C.bad,
                  color: '#ffffff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name="x" size={24} sw={3} />
              </span>
              <b style={{ fontSize: 20, lineHeight: 1.15, textAlign: 'center' }}>
                {tr('gate.v1.confirm.no')}
              </b>
            </button>
            <button
              type="button"
              onClick={() => onYes(f.photo_url ? null : newPhoto)}
              disabled={busy}
              aria-busy={busy || undefined}
              style={{
                ...big,
                background: C.okWash,
                border: `2.5px solid ${C.ok}`,
                color: C.ok,
                opacity: busy ? 0.7 : 1,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  background: C.ok,
                  color: '#ffffff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name="ok" size={26} sw={3} />
              </span>
              <b style={{ fontSize: 20, lineHeight: 1.15, textAlign: 'center' }}>
                {tr('gate.v1.confirm.yes')}
              </b>
            </button>
          </div>
        </footer>
      }
    >
      <div
        role="img"
        aria-label={tr('gate.v1.a11y.registeredPhoto')}
        style={{
          width: '100%',
          height: 300,
          borderRadius: 14,
          background: C.photo,
          color: C.photoInk,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        {f.photo_url ? (
          <img
            src={f.photo_url}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          // No photo on record: she may take one now (never required). It becomes his photo and
          // the office gets a FARMER_PHOTO_ADDED record.
          <V1Photo
            kind="person"
            value={newPhoto}
            onChange={setNewPhoto}
            render={({ preview, busy: taking, take }) => (
              <button
                type="button"
                onClick={take}
                disabled={taking}
                aria-label={tr(
                  newPhoto ? 'gate.v1.confirm.retakePhoto' : 'gate.v1.confirm.takePhoto',
                )}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 0,
                  background: 'transparent',
                  color: C.photoInk,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  cursor: 'pointer',
                  fontFamily: "'Mukta', sans-serif",
                  padding: 0,
                }}
              >
                {preview ? (
                  <img
                    src={preview}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <>
                    <Icon name="camera" size={72} sw={1.4} />
                    <b style={{ fontSize: 20, color: C.ink }}>{tr('gate.v1.confirm.takePhoto')}</b>
                    <span style={{ fontSize: 15 }}>{tr('gate.v1.confirm.noPhoto')}</span>
                  </>
                )}
              </button>
            )}
          />
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <span style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.25 }}>{f.name}</span>
          <span style={{ fontSize: 16, color: C.muted, lineHeight: 1.3 }}>
            {[f.father_name ? tr('gate.v1.father', { name: f.father_name }) : null, village]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </span>
        <b style={{ fontSize: 24 }}>{tr('gate.v1.farmerNo', { no: rosterShort(f.roster_no) })}</b>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Icon name="eye" size={24} style={{ color: C.muted }} />
        <span style={{ fontSize: 17, lineHeight: 1.3 }}>{tr('gate.v1.confirm.face')}</span>
      </div>
    </V1Screen>
  )
}
